import { useCurrentSceneIfAvailable } from "@/modes/mode-data-context";
import { selectBackgroundTasksBasedOnState } from "@/store/background-tasks/background-tasks-selector";
import {
  selectHasActiveSearchFilters,
  selectSearchDateRange,
  selectSearchTags,
  selectSearchText,
  selectSearchTextMatchingTagIds,
} from "@/store/project-filtering/project-filtering-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import { loadAnalysis } from "@/tools/analysis-saving-utils";
import { isPointCloudProcessingTask } from "@/utils/background-tasks";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { createSetOfAllTreeIds } from "@/utils/tree-generation/tree-generation";
import { FaroText, NoTranslate } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import { isIElementAnalysis } from "@faro-lotv/ielement-types";
import {
  selectAllIElementsOfType,
  selectAncestor,
  selectIElement,
  selectProjectLoadingState,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useForkRef } from "@mui/material";
import { Stack } from "@mui/system";
import { isEqual } from "@react-spring/shared";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { NodeApi, Tree, TreeApi } from "react-arborist";
import { TreeProps } from "react-arborist/dist/module/types/tree-props";
import { useDragDropManager } from "react-dnd";
import { useTreeContext } from "../tree-context";
import { TREE_NODE_HEIGHT } from "../tree-node";
import { isAreaSection, isRoomSection } from "./capture-tree-discriminator";
import { CaptureTreeDragCursor } from "./capture-tree-drag-cursor";
import { CaptureTreeRow } from "./capture-tree-row";
import { useCaptureTreeFiltering } from "./use-capture-tree-filtering";
import { useCaptureTreeMoveHandler } from "./use-capture-tree-move-handler";

type CaptureTreeProps = {
  /**
   * Set to true to disable all editing of the projects trough the tree view
   * TODO: To be implemented in https://faro01.atlassian.net/browse/SWEB-1950
   *
   * @default true
   */
  readOnly?: boolean;

  tree: TreeData[];

  ref?: React.Ref<TreeApi<TreeData>>;
} & Pick<TreeProps<TreeData>, "children" | "height">;

/**
 * @returns The tree representation of the capture data of the project,
 * providing multiple actions to the user to interact with the capture data
 */
export function CaptureTree({
  tree,
  height,
  children,
  ref,
}: CaptureTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const treeRef = useRef<TreeApi<TreeData>>(undefined);

  const forkedRef = useForkRef(ref, treeRef);

  const { openState, setOpenState } = useTreeContext();

  const { getState } = useAppStore();
  const dispatch = useAppDispatch();

  const activeElementId = useActiveTreeElement(tree);

  const { projectApiClient } = useApiClientContext();

  // Expand the whole tree when a search is executed.
  // Also need to wait for the project to be loaded in order to have access to all the potential nodes to expand.
  // The latter should be removable when https://faro01.atlassian.net/browse/SWEB-7394 is done.
  const shouldExpandTree = useAppSelector(selectHasActiveSearchFilters);
  const isLoading = useAppSelector(selectProjectLoadingState);
  useEffect(() => {
    if (shouldExpandTree && isLoading === "done") {
      treeRef.current?.openAll();
    }
  }, [shouldExpandTree, isLoading]);

  const onToggle = useCallback(() => {
    if (treeRef.current?.openState) {
      setOpenState(treeRef.current.openState);
    }
  }, [setOpenState]);

  useOpenParentOnPointCloudProcessed(treeRef);

  useEffect(() => {
    const store = getState();
    const analyses = selectAllIElementsOfType(
      isIElementAnalysis,
      activeElementId,
    )(store);
    if (analyses.length) {
      loadAnalysis(analyses, store, dispatch, projectApiClient);
    }
  }, [activeElementId, dispatch, projectApiClient, getState]);

  const isDragDropEnabled = useAppSelector(selectHasWritePermission);

  const disableDrag = useMemo(
    () => (isDragDropEnabled ? isNodeNotDraggable : () => true),
    [isDragDropEnabled],
  );
  const disableDrop = useMemo(
    () => (isDragDropEnabled ? isDropActionNotAllowed : () => true),
    [isDragDropEnabled],
  );

  const moveHandler = useCaptureTreeMoveHandler(tree);

  const hasActiveFilters = useAppSelector(selectHasActiveSearchFilters);
  const searchText = useAppSelector(selectSearchText);
  const filteringDateRange = useAppSelector(selectSearchDateRange);
  const filteringTags = useAppSelector(selectSearchTags);
  const textTagIds = useAppSelector(selectSearchTextMatchingTagIds);

  const filter = useCaptureTreeFiltering({
    hasActiveFilters,
    searchText,
    filteringDateRange,
    filteringTags,
    textTagIds,
  });

  const [isTreeEmpty, setIsTreeEmpty] = useState(false);
  useLayoutEffect(() => {
    setIsTreeEmpty(!treeRef.current?.visibleNodes.length);
  }, [searchText, filteringDateRange, filteringTags, textTagIds]);

  return (
    <Stack>
      {
        // Never translate the capture tree as there is mostly customer data in there and the capture tree will be
        // removed once we switch to the area navigation(see https://faro01.atlassian.net/browse/SWEB-4774)
      }
      <NoTranslate>
        <Tree<TreeData>
          dndManager={dragDropManager}
          ref={forkedRef}
          data={tree}
          selection={activeElementId}
          onMove={moveHandler}
          openByDefault={false}
          onToggle={onToggle}
          disableDrag={disableDrag}
          disableDrop={disableDrop}
          disableMultiSelection
          width="100%"
          // Hide tree via style to keep state
          height={isTreeEmpty ? 0 : height}
          rowHeight={TREE_NODE_HEIGHT}
          indent={24}
          initialOpenState={openState}
          renderCursor={CaptureTreeDragCursor}
          renderRow={CaptureTreeRow}
          // The value of the `searchTerm` prop of `Tree` controls if `searchMatch` is even called.
          // Setting this to any non-empty string will make sure that `searchMatch` is called.
          searchTerm={hasActiveFilters ? "hasActiveFilters" : undefined}
          searchMatch={filter}
        >
          {children}
        </Tree>
      </NoTranslate>

      {isTreeEmpty && (
        <Stack sx={{ alignItems: "center", mt: 3 }}>
          <FaroText variant="labelL">No filter results</FaroText>
        </Stack>
      )}
    </Stack>
  );
}

function isNodeNotDraggable(node: TreeData): boolean {
  const { element } = node;
  return !isAreaSection(element) && !isRoomSection(element);
}

type IsDropActionNotAllowedParams = {
  parentNode: NodeApi<TreeData>;
  dragNodes: Array<NodeApi<TreeData>>;
  index: number;
};

function isDropActionNotAllowed({
  parentNode,
  dragNodes,
  index,
}: IsDropActionNotAllowedParams): boolean {
  if (dragNodes.length !== 1) return true;
  const draggedElement = dragNodes[0].data.element;
  if (isAreaSection(draggedElement)) {
    return parentNode !== dragNodes[0].parent;
  } else if (isRoomSection(draggedElement)) {
    const nextSibling = parentNode.children?.at(index);
    return !(
      isAreaSection(parentNode.data.element) &&
      (!nextSibling ||
        !parentNode.isOpen ||
        isRoomSection(nextSibling.data.element))
    );
  }
  return true;
}

/**
 * @returns the id of the node that should be highlighted in the tree
 * @param tree the current tree
 */
function useActiveTreeElement(tree: TreeData[]): GUID | undefined {
  const { referenceElement, activeElement } =
    useCurrentSceneIfAvailable() ?? {};

  // Use the active element as reference if it belongs to
  // the subtree of the referenceElement
  const isReferenceElementAncestorOfActive = !!useAppSelector(
    selectAncestor(activeElement, (e) => e.id === referenceElement?.id),
  );

  const refElement =
    isReferenceElementAncestorOfActive || !referenceElement
      ? activeElement
      : referenceElement;

  const allIdsInTree = useMemo(() => createSetOfAllTreeIds(tree), [tree]);
  const firstAncestorInTree = useAppSelector(
    selectAncestor(refElement, (ancestor) => allIdsInTree.has(ancestor.id)),
  );

  return firstAncestorInTree?.id;
}

/**
 * @param treeRef Tree reference to open the parent node on successful point cloud processing
 * Opens the parent node in the tree once the latest uploaded point cloud is processed
 */
function useOpenParentOnPointCloudProcessed(
  treeRef: MutableRefObject<TreeApi<TreeData> | undefined>,
): void {
  const [visibleNodeIds, setVisibleNodeIds] = useState<GUID[]>([]);

  useEffect(() => {
    if (treeRef.current) {
      setVisibleNodeIds(treeRef.current.visibleNodes.map((node) => node.id));
    }
  }, [treeRef, treeRef.current?.visibleNodes]);

  const succeededPointCloudTasks = useAppSelector(
    (state) =>
      selectBackgroundTasksBasedOnState(BackgroundTaskState.succeeded)(
        state,
      ).filter(isPointCloudProcessingTask),
    isEqual,
  );

  const latestPointCloud = useAppSelector(
    selectIElement(succeededPointCloudTasks[0]?.iElementId),
  );

  const parentNode = useAppSelector(
    selectAncestor(latestPointCloud, (parent) =>
      visibleNodeIds.includes(parent.id),
    ),
  );

  useEffect(() => {
    if (parentNode) {
      treeRef.current?.open(parentNode.id);
    }
  }, [parentNode, treeRef]);
}
